// 지역 데이터 미리 받기(브라우저 연결부). bundles.ts 의 묶음 계획을 prefetch.ts 로 실행한다.
//  - 처음에 전부 한 번에(2026-10-27): 첫 진입 묶음 뒤에 지역2·3·4·마을②~④·화면 그림·영웅 단계까지 모두(약 42MB) 타이틀·마을·결과 화면에서 이어 받는다.
//    그 뒤로는 바뀐 파일(패치)만 받는다. 진행은 allDataStatus()로 한 줄에 보인다(타이틀 문구·마을 아래 가장자리 띠).
//  - 데이터 절약 모드·느린 회선(3G 이하)·오프라인·탭이 가려짐·던전 안(전투 프레임 보호)·인트로가 떠 있는 동안에는 한가할 때 받기를 끈다. 입장이 임박한 지역(문 앞 카드·입장 명령)은 이 조건과 무관하게 서두른다.
//  - 실패해도 게임은 기존 방식(들어갈 때 대기 화면 뒤에서 읽기)으로 그대로 간다 — 이 모듈은 캐시를 채울 뿐 로더를 막지 않는다.
import { hasManifest, manifestEntries, resolveAssetUrl, type AssetEntry } from './assetMap';
import { BUNDLE_IDS, prefetchPlan, bundleForRegion, type BundleId, type RegionId } from './bundles';
import { BundlePrefetcher, type BundleStatus, type PrefetchEnv } from './prefetch';

export type GameScene = 'title' | 'village' | 'dungeon' | 'result' | 'defeat' | 'boot';

export interface IdleConditions {
  scene: GameScene;
  introActive: boolean;
  hidden: boolean;
  online: boolean;
  saveData: boolean;
  /** navigator.connection.effectiveType ('4g'·'3g'…, 모르면 '') */
  effectiveType: string;
  /** 저장 공간 여유(모르면 생략) */
  storageRoom?: boolean;
}

/** 저장 공간 여유(순수): 남은 공간이 이만큼보다 적으면 한가할 때 미리 받기를 멈춘다(저장이 막혀 받아도 못 남기는 일을 피한다) */
export const MIN_FREE_BYTES = 150 * 1024 * 1024;
export function hasStorageRoom(est: { usage?: number; quota?: number } | null | undefined): boolean {
  if (!est || typeof est.quota !== 'number' || typeof est.usage !== 'number' || est.quota <= 0) return true; // 모르면 막지 않는다
  return est.quota - est.usage >= MIN_FREE_BYTES;
}

/** 한가할 때 미리 받아도 되는가(순수) */
export function canPrefetchIdle(c: IdleConditions): boolean {
  if (c.introActive || c.hidden || !c.online || c.saveData || c.storageRoom === false) return false;
  if (c.effectiveType === 'slow-2g' || c.effectiveType === '2g' || c.effectiveType === '3g') return false;
  return c.scene === 'village' || c.scene === 'title' || c.scene === 'result' || c.scene === 'defeat';
}

/** 받을 묶음 순서(전부 한 번에). effectiveType 은 호출 호환용(느린 회선은 canPrefetchIdle 이 이미 막는다) */
export function planForConditions(unlocked: readonly RegionId[], done: ReadonlySet<BundleId>, effectiveType: string): BundleId[] {
  void effectiveType;
  return prefetchPlan({ unlocked, done });
}

let pf: BundlePrefetcher | null = null;
let ctxScene: () => GameScene = () => 'boot';
let ctxUnlocked: () => RegionId[] = () => ['r1'];
let timer: ReturnType<typeof setInterval> | null = null;
/** 새 버전 적용 중: 한가할 때 받기 판정(tick)이 멈춤을 풀어 버리지 않게, 다시 열릴 때까지 아무것도 새로 요청하지 않는다 */
let held = false;
/** 마지막으로 잰 저장 공간 여유(비동기로 갱신, 모르면 true) */
let storageRoom = true;

interface NetInfo { saveData?: boolean; effectiveType?: string }
const netInfo = (): NetInfo => ((typeof navigator !== 'undefined' && (navigator as unknown as { connection?: NetInfo }).connection) || {}) as NetInfo;

function browserEnv(): PrefetchEnv {
  return {
    async fetchOne(url) {
      // priority: 게임이 같은 시각에 읽는 자원(기본 우선순위)에 회선을 먼저 내준다. 같은 주소를 게임이 읽으면 브라우저가 그 요청을 올려 합친다
      const r = await fetch(url, { credentials: 'same-origin', priority: 'low' } as RequestInit);
      if (!r.ok) return false;
      // 없는 파일에 SPA 대체가 index.html 200 을 돌려주면 받은 것으로 치지 않는다
      if ((r.headers.get('content-type') ?? '').includes('text/html')) return false;
      await r.arrayBuffer();
      return true;
    },
    async has(url) {
      if (typeof caches === 'undefined') return false;
      const m = await caches.match(url, { ignoreSearch: true });
      return !!m;
    },
    wait: (ms) => new Promise((r) => setTimeout(r, ms)),
    now: () => performance.now(),
  };
}

/** 표가 있는 빌드에서만 켠다(개발 서버는 표가 없어 아무 일도 하지 않는다). 한 번만 부른다 */
export function startRegionData(opts: { scene: () => GameScene; unlocked: () => RegionId[]; env?: PrefetchEnv; entries?: readonly AssetEntry[] }): BundlePrefetcher | null {
  if (pf) return pf;
  const entries = opts.entries ?? manifestEntries();
  if (!opts.entries && !hasManifest()) return null;
  ctxScene = opts.scene; ctxUnlocked = opts.unlocked;
  pf = new BundlePrefetcher(entries, (rel) => resolveAssetUrl('./assets/' + rel), opts.env ?? browserEnv());
  const tick = (): void => {
    if (!pf) return;
    if (held) return;
    try { void navigator.storage?.estimate?.().then((e) => { storageRoom = hasStorageRoom(e); }, () => {}); } catch { /* 모르면 막지 않는다 */ }
    const nav = netInfo();
    const intro = (typeof window !== 'undefined' ? (window as unknown as { __dhIntro?: { active?: boolean } }).__dhIntro : undefined);
    const idle = canPrefetchIdle({
      scene: ctxScene(), introActive: !!intro && intro.active === true,
      hidden: typeof document !== 'undefined' && document.visibilityState === 'hidden',
      online: typeof navigator === 'undefined' || navigator.onLine !== false,
      saveData: nav.saveData === true, effectiveType: String(nav.effectiveType ?? ''), storageRoom,
    });
    pf.setPaused(!idle);
    if (!idle) return;
    const done = new Set<BundleId>(BUNDLE_IDS.filter((b) => pf!.isDone(b)));
    const plan = planForConditions(ctxUnlocked(), done, String(nav.effectiveType ?? ''));
    // 남은 묶음을 모두 대기열에 올린다. 받는 순서는 묶음 순서(BundlePrefetcher.pick)라 앞 묶음부터 차례로 완성된다
    for (const b of plan) pf.request(b);
  };
  timer = setInterval(tick, 1500);
  tick();
  // QA 훅(?qa·개발 서버 전용): 묶음 상태 읽기·요청·멈춤
  if (typeof window !== 'undefined' && (new URLSearchParams(location.search).has('qa') || Boolean(import.meta.env.DEV))) {
    (window as unknown as { __DHP__: unknown }).__DHP__ = {
      status: (id: string) => pf?.status(id),
      request: (id: string, urgent = false) => pf?.request(id, urgent),
      pause: (p: boolean) => { if (timer) clearInterval(timer); timer = null; pf?.setPaused(p); },
    };
  }
  return pf;
}

/** 새 버전 적용 중에는 새 요청을 내지 않는다(급한 묶음 포함). false 로 다시 연다 */
export function holdRegionData(hold: boolean): void { held = hold; if (pf) pf.setPaused(hold, hold); }

export function stopRegionData(): void { if (timer) clearInterval(timer); timer = null; pf = null; }

/** 그 지역 데이터 상태. 표가 없거나 첫 진입 지역(r1)이면 항상 완료 */
export function regionDataStatus(region: RegionId): BundleStatus {
  const none: BundleStatus = { state: 'done', files: 0, filesDone: 0, bytesTotal: 0, bytesDone: 0, pct: 100 };
  if (!pf) return none;
  return pf.status(bundleForRegion(region));
}

/** 입장이 임박했다(문 앞 카드·입장 명령): 한가한 조건과 무관하게 이 지역을 먼저, 빠르게 받는다 */
export function prioritizeRegion(region: RegionId): void {
  if (!pf || held) return;
  pf.request(bundleForRegion(region), true);
}

/**
 * 문 앞에 섰을 뿐(아직 들어간다고 하지 않았다): 입장 명령과 달리 데이터 절약·느린 회선·저장 공간 부족에서는 받지 않고, 잠긴 지역은 받지 않는다.
 * 그 경우에도 들어가면 enterStage 가 prioritizeRegion 으로 받는다(로더가 어차피 읽는 양).
 */
export function warmRegion(region: RegionId, locked: boolean): void {
  if (locked) return;
  const nav = netInfo();
  const et = String(nav.effectiveType ?? '');
  if (nav.saveData === true || et === 'slow-2g' || et === '2g' || et === '3g' || !storageRoom) return;
  prioritizeRegion(region);
}

/** 패치 목록(서비스 워커가 활성화 때 남긴 "바뀐 파일")을 한가할 때 받는다 */
export function queuePatch(rels: readonly string[]): void {
  if (!pf || rels.length === 0) return;
  const set = new Set(rels);
  const entries = manifestEntries().filter((e) => set.has(e.rel));
  if (entries.length === 0) return;
  pf.addCustom('patch:update', entries);
  pf.request('patch:update');
}

/** 게임 데이터 전체(모든 묶음) 진행: 타이틀·마을 띠가 한 줄로 보인다. 표가 없으면(개발 서버) 완료 */
export function allDataStatus(): BundleStatus {
  let files = 0, filesDone = 0, bytesTotal = 0, bytesDone = 0, failed = false, running = false;
  if (!pf) return { state: 'done', files: 0, filesDone: 0, bytesTotal: 0, bytesDone: 0, pct: 100 };
  for (const id of BUNDLE_IDS) {
    const s = pf.status(id);
    files += s.files; filesDone += s.filesDone; bytesTotal += s.bytesTotal;
    bytesDone += s.state === 'done' ? s.bytesTotal : s.bytesDone;
    if (s.state === 'failed') failed = true;
    if (s.state === 'running' || s.state === 'queued') running = true;
  }
  const done = bytesDone >= bytesTotal;
  const pct = bytesTotal <= 0 || done ? 100 : Math.min(99, Math.floor((bytesDone / bytesTotal) * 100));
  return { state: done ? 'done' : failed && !running ? 'failed' : running ? 'running' : 'idle', files, filesDone, bytesTotal, bytesDone, pct };
}

export function patchStatus(): BundleStatus { return pf ? pf.status('patch:update') : { state: 'done', files: 0, filesDone: 0, bytesTotal: 0, bytesDone: 0, pct: 100 }; }

export function onRegionDataChange(fn: () => void): () => void { return pf ? pf.onChange(fn) : () => {}; }

/** 'r2-s05' · 'h3-s02' 같은 스테이지 id 에서 지역을 뽑는다(없으면 null) */
export function regionOfStage(stage: string | undefined | null): RegionId | null {
  const m = /^[rhx]([1-6])-s\d+$/.exec(stage ?? '');
  return m ? (('r' + m[1]) as RegionId) : null;
}
